import type { ActivityInput } from '@orus.eu/activity'
import {
  AmountDimension,
  BaseDimension,
  BooleanDimension,
  defineProductDimensions,
  ExclusionDimension,
  FinancialRateDimension,
  FinancialRateDimensionWithRetrocompatibleInstallmentFeeRateDimension,
  FinancialRateWithUnsetDimension,
  rcpaxaPricingVersionDimension,
  rcpaxaSelectedDimension,
  StringsEnumDimension,
} from '@orus.eu/dimensions'
import { rcpaxaFormules } from '@orus.eu/pricing'
import { rcpaxaPricingCategories, rcpaxaProductVersions } from '@orus.eu/product'

import { rcpaxaQuoteDimension } from './rcpaxa-quote/rcpaxa-quote-dimension.ts'

export * from './rcpaxa-activity-vocabulary.ts'
export * from './rcpaxa-guarantee-text.ts'
export * from './rcpaxa-quote/index.ts'

export const rcpaxaAvailableDimension = new BooleanDimension({
  name: 'rcpaxaAvailable',
  displayKeys: { name: 'dimension_name_rcpaxa_available' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaProductVersionDimension = new StringsEnumDimension({
  name: 'rcpaxaProductVersion',
  displayKeys: { name: 'dimension_name_rcpaxa_product_version' },
  values: rcpaxaProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
})

export const rcpaxaExclusionsDimension = new ExclusionDimension({
  name: 'rcpaxaExclusions',
  displayKeys: { name: 'dimension_name_rcpaxa_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
})

export const rcpaxaActivitiesDimension = new BaseDimension<'rcpaxaActivities', ActivityInput[]>({
  name: 'rcpaxaActivities',
  displayKeys: { name: 'dimension_name_rcpaxa_activities' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const defaultRcpaxaManagementFeeRateDimension = new FinancialRateDimension({
  name: 'defaultRcpaxaManagementFeeRate',
  displayKeys: { name: 'dimension_name_default_rcpaxa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetRcpaxaManagementFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetRcpaxaManagementFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_rcpaxa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaManagementFeeRateDimension = new FinancialRateDimension({
  name: 'rcpaxaManagementFeeRate',
  displayKeys: { name: 'dimension_name_rcpaxa_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const defaultRcpaxaInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'defaultRcpaxaInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_default_rcpaxa_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const manuallySetRcpaxaInstallmentFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetRcpaxaInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_rcpaxa_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaInstallmentFeeRateDimension =
  new FinancialRateDimensionWithRetrocompatibleInstallmentFeeRateDimension({
    name: 'rcpaxaInstallmentFeeRate',
    displayKeys: { name: 'dimension_name_rcpaxa_installment_fee_rate' },
    considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
    includeInTrackingMessages: false,
    inputLocking: undefined,
  } as const)

export const rcpaxaFormuleDimension = new StringsEnumDimension({
  name: 'rcpaxaFormule',
  displayKeys: { name: 'dimension_name_rcpaxa_formule' },
  values: rcpaxaFormules,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaPricingCategoryDimension = new StringsEnumDimension({
  name: 'rcpaxaPricingCategory',
  displayKeys: { name: 'dimension_name_rcpaxa_pricing_category' },
  values: rcpaxaPricingCategories,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaCorporalDamageLoiDimension = new AmountDimension({
  name: 'rcpaxaCorporalDamageLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_corporal_damage_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaMaterialDamageLoiDimension = new AmountDimension({
  name: 'rcpaxaMaterialDamageLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_material_damage_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const rcpaxaMaterialDamageDeductibleDimension = new AmountDimension({
  name: 'rcpaxaMaterialDamageDeductible',
  displayKeys: { name: 'dimension_name_rcpaxa_material_damage_deductible' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaDincLoiDimension = new AmountDimension({
  name: 'rcpaxaDincLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_dinc_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const rcpaxaDincDeductibleDimension = new AmountDimension({
  name: 'rcpaxaDincDeductible',
  displayKeys: { name: 'dimension_name_rcpaxa_dinc_deductible' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaEntrustedGoodsLoiDimension = new AmountDimension({
  name: 'rcpaxaEntrustedGoodsLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_entrusted_goods_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const rcpaxaEntrustedGoodsDeductibleDimension = new AmountDimension({
  name: 'rcpaxaEntrustedGoodsDeductible',
  displayKeys: { name: 'dimension_name_rcpaxa_entrusted_goods_deductible' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaRcEnvAccidentalLoiDimension = new AmountDimension({
  name: 'rcpaxaRcEnvAccidentalLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_rc_env_accidental_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const rcpaxaRcEnvAccidentalDeductibleDimension = new AmountDimension({
  name: 'rcpaxaRcEnvAccidentalDeductible',
  displayKeys: { name: 'dimension_name_rcpaxa_rc_env_accidental_deductible' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaRcEnvPrejudiceLoiDimension = new AmountDimension({
  name: 'rcpaxaRcEnvPrejudiceLoi',
  displayKeys: { name: 'dimension_name_rcpaxa_rc_env_prejudice_loi' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const rcpaxaRcEnvPrejudiceDeductibleDimension = new AmountDimension({
  name: 'rcpaxaRcEnvPrejudiceDeductible',
  displayKeys: { name: 'dimension_name_rcpaxa_rc_env_prejudice_deductible' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const rcpaxaDimensionsModule = defineProductDimensions({
  product: 'rcpaxa',
  family: null,
  selectionDimension: rcpaxaSelectedDimension,
  productVersionDimension: rcpaxaProductVersionDimension,
  pricingVersionDimension: rcpaxaPricingVersionDimension,
  quoteV3Dimension: rcpaxaQuoteDimension,
})
